import { useEffect, useState } from 'react';
import { Download, WifiOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * Registers the service worker and surfaces two UX affordances:
 * - "siap offline" once the app shell has been precached;
 * - "versi baru tersedia" with a reload action when an update is waiting.
 * Renders nothing itself.
 */
export function PwaManager() {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // Poll for updates hourly so long-lived tabs pick up new deploys.
      if (registration) {
        setInterval(() => registration.update().catch(() => undefined), 60 * 60 * 1000);
      }
    },
  });

  useEffect(() => {
    if (offlineReady) {
      toast.success('Aplikasi siap digunakan secara offline.', { id: 'sw-offline-ready' });
      setOfflineReady(false);
    }
  }, [offlineReady, setOfflineReady]);

  useEffect(() => {
    if (needRefresh) {
      toast('Versi baru TIRTA SMART tersedia', {
        id: 'sw-update',
        duration: Infinity,
        description: 'Muat ulang untuk memakai versi terbaru.',
        action: {
          label: 'Muat ulang',
          onClick: () => updateServiceWorker(true),
        },
        onDismiss: () => setNeedRefresh(false),
      });
    }
  }, [needRefresh, setNeedRefresh, updateServiceWorker]);

  return null;
}

/** App-install button; only rendered when the browser deems the app installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function InstallButton({
  className,
  variant = 'outline',
  size = 'sm',
  showLabel = true,
}: {
  className?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'md' | 'icon';
  showLabel?: boolean;
}) {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone()) return;
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setPromptEvent(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!promptEvent) return null;

  const handleInstall = async () => {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === 'accepted') {
      toast.success('Terima kasih! TIRTA SMART dipasang di perangkat Anda.');
    }
    setPromptEvent(null);
  };

  return (
    <Button
      variant={variant}
      size={showLabel ? size : 'icon'}
      className={className}
      onClick={handleInstall}
      title="Pasang aplikasi TIRTA SMART"
      aria-label="Pasang aplikasi TIRTA SMART"
    >
      <Download className="h-4 w-4" />
      {showLabel ? 'Pasang Aplikasi' : null}
    </Button>
  );
}

/** Slim banner shown while the device is offline. */
export function OfflineBanner() {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => {
      setOffline(true);
      setDismissed(false);
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (!offline || dismissed) return null;

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] flex items-center justify-center gap-2 px-4 py-2 pb-safe text-sm font-medium',
        'bg-amber-500 text-amber-950 shadow-pop',
      )}
      role="status"
    >
      <WifiOff className="h-4 w-4" />
      <span>Anda sedang offline. Sebagian data mungkin belum terbaru.</span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-2 rounded p-0.5 hover:bg-amber-600/20"
        aria-label="Tutup"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
